import {useHalState, useHalStates} from '@synako/haligator';
import {PanierFound} from './PanierFound';
import {PanierNotFound} from './PanierNotFound';
import {useLivraisonsPossiblesContext, usePanier} from 'ff-common';
import {Trans, useTranslation} from 'react-i18next';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {Text} from '@chakra-ui/react';
import {useParams} from 'react-router-dom';
import {useEffect, useRef} from 'react';
import {useLivraisons} from '@/haligator-factory';

export const PanierView: React.FC = () => {
  const queryParams = new URLSearchParams(location.search);
  const derniereAction = queryParams.get('derniereAction');
  const logique = queryParams.get('logique');
  const panierNav = usePanier();
  const {state: panier, loading} = useHalState(panierNav);

  // Sur /panier/:date_panier_actif, la date de l'URL fait foi : en arrivant par un
  // chargement complet (redirection depuis le BO, F5, lien partagé), la livraison
  // sélectionnée en contexte est celle par défaut (la prochaine), pas celle de l'URL.
  // On resynchronise la sélection sur la livraison correspondant à la date de l'URL.
  const {date_panier_actif} = useParams<{date_panier_actif: string}>();
  const {livraisonSelectionnee, setLivraisonSelectionnee} =
    useLivraisonsPossiblesContext();
  const livraisonsPossibles = useHalStates(useLivraisons().followAll('items'));
  // `dateUrlAlignee` mémorise la dernière date d'URL déjà traitée : on aligne la sélection
  // à l'arrivée sur une nouvelle URL, mais on ne resynchronise pas en continu. Un clic sur un
  // onglet sélectionne la livraison avant que l'URL ne suive ; sans cette ref, un rejeu de l'effet
  // dans cette fenêtre (rafraîchissement des livraisons) renverrait la sélection sur l'onglet quitté.
  const dateSelectionnee = livraisonSelectionnee.data.tolede.date_de_livraison;
  const dateUrlAlignee = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!date_panier_actif || dateUrlAlignee.current === date_panier_actif) {
      return;
    }
    const livraisonDeLUrl = livraisonsPossibles.states?.find(
      (livraison) =>
        livraison.data.tolede.date_de_livraison === date_panier_actif,
    );
    // Livraisons pas encore chargées : on réessaie quand elles arrivent.
    if (!livraisonDeLUrl) return;
    dateUrlAlignee.current = date_panier_actif;
    if (date_panier_actif !== dateSelectionnee) {
      setLivraisonSelectionnee(livraisonDeLUrl);
    }
  }, [
    date_panier_actif,
    dateSelectionnee,
    livraisonsPossibles.states,
    setLivraisonSelectionnee,
  ]);
  const {t} = useTranslation();
  const textToast = useSimpleTextToast();

  // A la validation d'un panier, la redirection vers PanierView empêche l'affichage du toast
  if (logique === 'avertissement') {
    textToast(
      <Trans
        t={t}
        components={{strong: <Text style={{fontWeight: 700}}></Text>}}
      >
        {t('toast.panier_valide_safelive')}
      </Trans>,
      {
        id: 'avertissement',
        duration: 4000,
      },
    );
  } else if (logique === 'simulation') {
    textToast(
      <Trans
        t={t}
        components={{strong: <Text style={{fontWeight: 700}}></Text>}}
      >
        {t('toast.panier_valide_simulation')}
      </Trans>,
      {
        id: 'simulation',
        duration: 4000,
      },
    );
  }

  if (loading && !panier) {
    return null;
  }
  if (panier) {
    return <PanierFound panier={panierNav} />;
  }
  return <PanierNotFound derniereAction={derniereAction} />;
};
